/* ==========================================================================
   Alloi Skin — Media Contrast Detection
   ==========================================================================
   Companion to assets/alloi-media-contrast.css.

   Problem
   -------
   Text colour is chosen by the Night/Milk toggle (body[data-theme]). Over a
   flat page background that is correct. Over media it is a coin flip: a
   headline on a dark video renders #262626 on #1a1a1a the moment a visitor
   switches to Milk. sections/alloi_hero.liquid solves this for one section by
   hand-writing a forced-Night token block, and Alloi_header.liquid keys off a
   .alloi-hero-visible class the hero's IntersectionObserver toggles. Both work,
   and neither generalises — every new section over media needs the same block
   written again, keyed to a backdrop somebody eyeballed.

   What this does
   --------------
   Measures the backdrop actually painted behind a run of text and writes the
   verdict to data-on-media on the content wrapper. The CSS file turns that into
   whichever token set reads against the backdrop, ignoring the theme toggle.

   Author controls (set in Liquid, on the section or any ancestor):
     data-media-contrast="off"           skip this subtree entirely
     data-media-contrast="dark"|"light"  force the verdict, skip measurement

   Debug: window.ALLOI_MEDIA_CONTRAST_DEBUG = true  before this file runs, or
   call window.alloiMediaContrast.refresh() from the console.
   ========================================================================== */

(function () {
  'use strict';

  if (window.alloiMediaContrast) return;

  /* ------------------------------------------------------------------------
     Constants
     ---------------------------------------------------------------------- */

  var MILK = [237, 237, 226];
  var NIGHT = [38, 38, 38];

  /* Treated as fully opaque — below this a layer is composited, above it the
     layer is the surface and nothing behind it can matter. */
  var OPAQUE = 0.98;

  /* Width in px of the downsampled copy fetched for measurement. Small enough
     to be a rounding error on the wire (~1-2KB, and the CDN caches it), large
     enough that an object-fit: cover crop still leaves usable pixels. */
  var SAMPLE_W = 64;

  /* A layer must cover at least this share of the text box to count as its
     backdrop. Below it the text is mostly over something else. */
  var COVERAGE = 0.55;

  /* Ignore media too small to be a backdrop — icons, swatches, logos. */
  var MIN_MEDIA = 100;

  /* Stacking sentinel for an ancestor's own background: below any real
     z-index, and finite so the sort comparator can subtract it. */
  var Z_ANCESTOR = -1e9;

  var DEBUG = !!window.ALLOI_MEDIA_CONTRAST_DEBUG;

  /* ------------------------------------------------------------------------
     Colour
     ---------------------------------------------------------------------- */

  function toLinear(v) {
    v /= 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }

  /* WCAG relative luminance. */
  function luminance(rgb) {
    return 0.2126 * toLinear(rgb[0]) + 0.7152 * toLinear(rgb[1]) + 0.0722 * toLinear(rgb[2]);
  }

  function contrast(l1, l2) {
    var hi = Math.max(l1, l2);
    var lo = Math.min(l1, l2);
    return (hi + 0.05) / (lo + 0.05);
  }

  var L_MILK = luminance(MILK);
  var L_NIGHT = luminance(NIGHT);

  /* Parse rgb()/rgba() as produced by getComputedStyle. Colours only ever
     reach us through computed styles, which normalise to these two forms, so
     hex/hsl/named parsing is not needed. */
  function parseColor(str) {
    var m = /rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.%]+))?\s*\)/.exec(str || '');
    if (!m) return null;
    var a = m[4] === undefined ? 1 : (/%$/.test(m[4]) ? parseFloat(m[4]) / 100 : parseFloat(m[4]));
    return [+m[1], +m[2], +m[3], isNaN(a) ? 1 : a];
  }

  /* src-over: paint `top` (with alpha) onto opaque `base`. */
  function composite(base, top) {
    var a = top[3];
    return [
      top[0] * a + base[0] * (1 - a),
      top[1] * a + base[1] * (1 - a),
      top[2] * a + base[2] * (1 - a),
    ];
  }

  /* ------------------------------------------------------------------------
     Geometry
     ---------------------------------------------------------------------- */

  function coverage(inner, outer) {
    var w = Math.min(inner.right, outer.right) - Math.max(inner.left, outer.left);
    var h = Math.min(inner.bottom, outer.bottom) - Math.max(inner.top, outer.top);
    if (w <= 0 || h <= 0) return 0;
    var area = inner.width * inner.height;
    return area > 0 ? (w * h) / area : 0;
  }

  function visible(el) {
    var node = el;
    while (node && node.nodeType === 1 && node !== document.documentElement) {
      var cs = getComputedStyle(node);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
      node = node.parentElement;
    }
    return true;
  }

  /* ------------------------------------------------------------------------
     Media sampling

     The in-page <img> cannot be reused: drawing it to a canvas taints the
     canvas and getImageData throws SecurityError. cdn.shopify.com does send
     access-control-allow-origin: *, so a second Image with crossOrigin set
     loads clean and reads back fine. Verified against the live storefront.
     ---------------------------------------------------------------------- */

  var sampleCache = new Map();

  /* Same bitmaps, readable synchronously. Scroll tracking re-measures every
     frame and cannot await a promise it already resolved. */
  var decoded = new Map();

  function downsampledUrl(src, width) {
    try {
      var url = new URL(src, location.href);
      /* Shopify's image CDN resizes via ?width=. Anything else is fetched as
         served — still correct, just not as cheap. */
      if (/(^|\.)shopify\.com$/.test(url.hostname) || /\/cdn\/shop\//.test(url.pathname)) {
        url.searchParams.set('width', String(width));
      }
      return url.href;
    } catch (e) {
      return src;
    }
  }

  function loadSample(src) {
    if (sampleCache.has(src)) return sampleCache.get(src);

    var p = new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () { decoded.set(src, img); resolve(img); };
      img.onerror = function () { reject(new Error('load failed')); };
      img.src = downsampledUrl(src, SAMPLE_W);
    });

    /* Drop failures so a transient error does not pin a rejected promise in
       the cache for the life of the page (and re-throw for the caller). */
    p.catch(function () { sampleCache.delete(src); });

    sampleCache.set(src, p);
    return p;
  }

  /* Mean luminance of the part of `source` that sits behind `textRect`.

     `source` is drawn into a canvas laid out exactly like the media element's
     box — object-fit crop applied — so canvas space and viewport space differ
     only by a scale factor, and the text box maps across with a linear
     transform instead of per-fit special cases at read time. */
  function regionLuminance(source, mediaRect, textRect, fit, position) {
    var sw = source.naturalWidth || source.videoWidth || 0;
    var sh = source.naturalHeight || source.videoHeight || 0;
    if (!sw || !sh || !mediaRect.width || !mediaRect.height) return null;

    var cw = SAMPLE_W;
    var ch = Math.max(1, Math.round(SAMPLE_W * (mediaRect.height / mediaRect.width)));

    var canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    /* Source rect to lift out of the bitmap so it fills the box the way the
       browser fills it. */
    var sx = 0, sy = 0, sWidth = sw, sHeight = sh;
    var boxAR = mediaRect.width / mediaRect.height;
    var imgAR = sw / sh;

    if (fit === 'cover' || fit === 'none' || fit === 'scale-down') {
      if (imgAR > boxAR) {
        sWidth = sh * boxAR;
        sx = (sw - sWidth) * position.x;
      } else {
        sHeight = sw / boxAR;
        sy = (sh - sHeight) * position.y;
      }
    }
    /* 'fill' stretches — the whole bitmap maps to the whole box, which is the
       default sx/sy/sWidth/sHeight above. 'contain' letterboxes; averaging the
       whole bitmap is close enough and avoids guessing the letterbox colour. */

    try {
      ctx.drawImage(source, sx, sy, sWidth, sHeight, 0, 0, cw, ch);
    } catch (e) {
      return null;
    }

    /* Text box -> canvas box. */
    var scaleX = cw / mediaRect.width;
    var scaleY = ch / mediaRect.height;
    var x0 = Math.floor((Math.max(textRect.left, mediaRect.left) - mediaRect.left) * scaleX);
    var y0 = Math.floor((Math.max(textRect.top, mediaRect.top) - mediaRect.top) * scaleY);
    var x1 = Math.ceil((Math.min(textRect.right, mediaRect.right) - mediaRect.left) * scaleX);
    var y1 = Math.ceil((Math.min(textRect.bottom, mediaRect.bottom) - mediaRect.top) * scaleY);

    x0 = Math.max(0, Math.min(cw - 1, x0));
    y0 = Math.max(0, Math.min(ch - 1, y0));
    x1 = Math.max(x0 + 1, Math.min(cw, x1));
    y1 = Math.max(y0 + 1, Math.min(ch, y1));

    var data;
    try {
      data = ctx.getImageData(x0, y0, x1 - x0, y1 - y0).data;
    } catch (e) {
      /* Tainted canvas — the media is not CORS-readable. */
      if (DEBUG) console.warn('[alloi-media-contrast] canvas tainted', e.message);
      return null;
    }

    var r = 0, g = 0, b = 0, n = 0;
    for (var i = 0; i < data.length; i += 4) {
      var a = data[i + 3] / 255;
      r += data[i] * a;
      g += data[i + 1] * a;
      b += data[i + 2] * a;
      n++;
    }
    if (!n) return null;
    return [r / n, g / n, b / n];
  }

  function objectPosition(cs) {
    var parts = (cs.objectPosition || '50% 50%').split(/\s+/);
    var pct = function (v) {
      if (/%$/.test(v)) return parseFloat(v) / 100;
      if (v === 'left' || v === 'top') return 0;
      if (v === 'right' || v === 'bottom') return 1;
      if (v === 'center') return 0.5;
      return 0.5;
    };
    return { x: pct(parts[0] || '50%'), y: pct(parts[1] || parts[0] || '50%') };
  }

  /* ------------------------------------------------------------------------
     Paint model

     document.elementsFromPoint looked like the obvious hit test and is not
     usable here: it skips pointer-events: none elements, and snippets/
     Alloi_overlay.liquid sets exactly that on every scrim. The scrims are the
     layers that most change the answer, so they cannot be the ones we miss.

     Instead: walk up from the text collecting every box that covers it and is
     painted by something other than the text's own ancestry, stopping at the
     first opaque surface. Layers come back bottom-first.
     ---------------------------------------------------------------------- */

  function backgroundImageUrl(cs) {
    var m = /url\((['"]?)(.*?)\1\)/.exec(cs.backgroundImage || '');
    return m ? m[2] : null;
  }

  /* Paint contributed by one element, ignoring its descendants. */
  function ownPaint(el, cs) {
    var tag = el.tagName.toLowerCase();
    if (tag === 'img' || tag === 'video') {
      var src = tag === 'video' ? posterFor(el) : (el.currentSrc || el.src);
      if (src) return { kind: 'media', el: el, src: src };
      return null;
    }
    var bgUrl = backgroundImageUrl(cs);
    if (bgUrl && !/^data:/.test(bgUrl)) return { kind: 'media', el: el, src: bgUrl };

    var color = parseColor(cs.backgroundColor);
    if (color && color[3] > 0) return { kind: 'color', el: el, color: color };
    return null;
  }

  /* A <video> frame is only drawable once it has data, and HLS sources spend
     the first moments at readyState 0. The templates always render a preview
     image alongside, so prefer that: it is a plain CDN image, always ready. */
  function posterFor(video) {
    if (video.poster) return video.poster;
    var parent = video.parentElement;
    if (parent) {
      var img = parent.querySelector('img');
      if (img && (img.currentSrc || img.src)) return img.currentSrc || img.src;
    }
    return null;
  }

  /* Collect painted boxes inside `root` that cover `rect`, skipping the
     subtree the text lives in. */
  function collectFrom(root, rect, skip, out, depth) {
    if (depth > 6 || !root || root.nodeType !== 1) return;
    for (var i = 0; i < root.children.length; i++) {
      var child = root.children[i];
      if (child === skip || child.contains(skip)) continue;
      if (!visible(child)) continue;

      var box = child.getBoundingClientRect();
      if (coverage(rect, box) < COVERAGE) continue;

      var cs = getComputedStyle(child);
      var paint = ownPaint(child, cs);
      if (paint) {
        paint.z = cs.zIndex === 'auto' ? 0 : parseInt(cs.zIndex, 10) || 0;
        paint.order = out.length;
        out.push(paint);
        if (paint.kind === 'color' && paint.color[3] >= OPAQUE) continue;
      }
      collectFrom(child, rect, skip, out, depth + 1);
    }
  }

  /* `rect` defaults to the anchor's own box. A fixed element passes an
     explicit probe box instead, because what sits under it is not its own
     geometry and is not in its ancestry either. */
  /* `limit` caps the upward walk. Text in the flow has no limit — its media is
     usually a near sibling, so the walk ends quickly at the first opaque
     surface. A probe under a fixed bar starts from a deep anchor and would
     otherwise climb to <body>, running the recursive collector against most of
     the page at the top levels. Capping it at the enclosing section keeps the
     cost proportional to that section. */
  function resolveLayers(anchorEl, rect, limit) {
    rect = rect || anchorEl.getBoundingClientRect();
    var layers = [];
    var node = anchorEl;
    var base = null;
    var ancestorOrder = -1;

    while (node && node.parentElement && node !== document.body) {
      var parent = node.parentElement;

      /* Siblings and their subtrees paint behind the text's own branch. */
      collectFrom(parent, rect, node, layers, 0);

      var pcs = getComputedStyle(parent);
      var paint = ownPaint(parent, pcs);
      if (paint) {
        /* An ancestor's own background paints below its children, and the
           further up the chain the lower it sits — hence a distinct, ever
           more negative order rather than a shared sentinel. Finite: the
           comparator subtracts these, and -Infinity - -Infinity is NaN. */
        paint.z = Z_ANCESTOR;
        paint.order = ancestorOrder--;
        layers.push(paint);
        if (paint.kind === 'color' && paint.color[3] >= OPAQUE) { base = paint; break; }
        if (paint.kind === 'media') break;
      }
      if (limit && parent === limit) break;
      node = parent;
    }

    if (!base) {
      var bodyColor = parseColor(getComputedStyle(document.body).backgroundColor);
      if (!bodyColor || bodyColor[3] < OPAQUE) {
        bodyColor = parseColor(getComputedStyle(document.documentElement).backgroundColor);
      }
      base = { kind: 'color', color: bodyColor && bodyColor[3] >= OPAQUE ? bodyColor : [255, 255, 255, 1] };
    }

    layers.sort(function (a, b) { return a.z - b.z || a.order - b.order; });
    return { rect: rect, base: base, layers: layers };
  }

  /* ------------------------------------------------------------------------
     Verdict
     ---------------------------------------------------------------------- */

  function decide(rgb) {
    var l = luminance(rgb);
    /* Pick the token set that reads better rather than tripping a fixed
       threshold — that keeps mid-greys honest instead of snapping at 50%. */
    return contrast(l, L_MILK) >= contrast(l, L_NIGHT) ? 'dark' : 'light';
  }

  /* Everything up to "which bitmap do we need" is synchronous DOM work — on a
     full homepage it costs about 4ms. Fetching the bitmaps is what costs, so
     planning is kept separate from sampling: plan every run of text first,
     fetch the distinct sources once and in parallel, then finish. Doing it
     inline turned ~10 cache-cold CDN round trips into a 2.3s serial chain. */
  function plan(anchorEl, rect, limit) {
    var resolved = resolveLayers(anchorEl, rect, limit);
    var mediaLayers = resolved.layers.filter(function (l) { return l.kind === 'media'; });
    if (!mediaLayers.length) return null;

    /* Anything smaller than a backdrop is decoration, not a surface. Layers
       are sorted bottom-first, so the last survivor is the topmost media —
       the one actually visible behind the text. */
    var topMedia = null;
    for (var i = 0; i < mediaLayers.length; i++) {
      var box = mediaLayers[i].el.getBoundingClientRect();
      if (box.width < MIN_MEDIA || box.height < MIN_MEDIA) continue;
      topMedia = mediaLayers[i];
    }
    if (!topMedia) return null;

    return { el: anchorEl, resolved: resolved, topMedia: topMedia };
  }

  function finish(job, source) {
    if (!source) return null;

    var topMedia = job.topMedia;
    var cs = getComputedStyle(topMedia.el);
    var rgb = regionLuminance(
      source,
      topMedia.el.getBoundingClientRect(),
      job.resolved.rect,
      cs.objectFit || 'cover',
      objectPosition(cs)
    );
    if (!rgb) return null;

    /* Composite everything painted above the media, in paint order. */
    var reached = false;
    var layers = job.resolved.layers;
    for (var j = 0; j < layers.length; j++) {
      var layer = layers[j];
      if (layer === topMedia) { reached = true; continue; }
      if (!reached) continue;
      if (layer.kind === 'color') rgb = composite(rgb, layer.color);
    }

    return { verdict: decide(rgb), rgb: rgb, media: topMedia.src, mediaEl: topMedia.el };
  }

  /* Convenience wrapper for one-off use from the console. */
  async function measure(textEl) {
    var job = plan(textEl);
    if (!job) return null;
    var source = null;
    try {
      source = await loadSample(job.topMedia.src);
    } catch (e) {
      if (DEBUG) console.warn('[alloi-media-contrast] sample failed', job.topMedia.src, e.message);
    }
    return finish(job, source);
  }

  /* ------------------------------------------------------------------------
     Discovery
     ---------------------------------------------------------------------- */

  function textLeaves(root) {
    var out = [];
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
      acceptNode: function (el) {
        /* SVG elements report a lowercase tagName, HTML elements uppercase. */
        var tag = el.tagName.toUpperCase();
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'SVG' || tag === 'TEMPLATE') {
          return NodeFilter.FILTER_REJECT;
        }
        for (var i = 0; i < el.childNodes.length; i++) {
          var n = el.childNodes[i];
          if (n.nodeType === 3 && n.textContent.trim().length > 1) return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      },
    });
    var node;
    while ((node = walker.nextNode())) out.push(node);
    return out;
  }

  /* The wrapper that should carry the verdict: the outermost ancestor still
     over the same media and not painting a surface of its own. Setting it
     there rather than on the text leaf lets buttons, rules and secondary text
     inside the same overlay inherit the tokens too. */
  function contentRoot(textEl, mediaEl) {
    var node = textEl;
    while (node.parentElement && node.parentElement !== document.body) {
      var parent = node.parentElement;
      if (parent.contains(mediaEl)) break;
      var cs = getComputedStyle(parent);
      var color = parseColor(cs.backgroundColor);
      if (color && color[3] >= OPAQUE) break;
      if (backgroundImageUrl(cs)) break;
      node = parent;
    }
    return node;
  }

  /* Boxes big enough to act as a backdrop, used to throw out the majority of
     text before any per-element style work happens. */
  function mediaBoxes(root) {
    var out = [];
    var nodes = root.querySelectorAll('img, video');
    for (var i = 0; i < nodes.length; i++) {
      var box = nodes[i].getBoundingClientRect();
      if (box.width >= MIN_MEDIA && box.height >= MIN_MEDIA) out.push(box);
    }
    return out;
  }

  function intersects(a, b) {
    return Math.min(a.right, b.right) > Math.max(a.left, b.left) &&
      Math.min(a.bottom, b.bottom) > Math.max(a.top, b.top);
  }

  async function scan(root) {
    root = root || document.body;

    var boxes = mediaBoxes(root);
    if (!boxes.length) return;

    /* Phase 1 — plan (synchronous). */
    var jobs = [];
    var leaves = textLeaves(root);

    for (var i = 0; i < leaves.length; i++) {
      var el = leaves[i];

      var forced = el.closest('[data-media-contrast]');
      if (forced) {
        var mode = forced.getAttribute('data-media-contrast');
        if (mode === 'off') continue;
        if (mode === 'dark' || mode === 'light') {
          forced.setAttribute('data-on-media', mode);
          continue;
        }
      }

      var rect = el.getBoundingClientRect();
      if (rect.width < 20 || rect.height < 6) continue;

      var overlapsMedia = false;
      for (var b = 0; b < boxes.length; b++) {
        if (intersects(rect, boxes[b])) { overlapsMedia = true; break; }
      }
      if (!overlapsMedia) continue;

      if (!visible(el)) continue;

      var job = plan(el);
      if (job) jobs.push(job);
    }

    if (!jobs.length) return;

    /* Phase 2 — fetch every distinct bitmap at once. */
    var sources = {};
    jobs.forEach(function (j) { sources[j.topMedia.src] = true; });

    var loaded = new Map();
    await Promise.all(Object.keys(sources).map(function (src) {
      return loadSample(src).then(
        function (img) { loaded.set(src, img); },
        function (e) { if (DEBUG) console.warn('[alloi-media-contrast] sample failed', src, e.message); }
      );
    }));

    /* Phase 3 — decide and apply. */
    var handled = new Set();
    for (var k = 0; k < jobs.length; k++) {
      var result = finish(jobs[k], loaded.get(jobs[k].topMedia.src));
      if (!result) continue;

      var wrapper = contentRoot(jobs[k].el, result.mediaEl);
      if (handled.has(wrapper)) continue;
      handled.add(wrapper);

      wrapper.setAttribute('data-on-media', result.verdict);
      if (DEBUG) {
        console.log('[alloi-media-contrast]', result.verdict,
          'L=' + luminance(result.rgb).toFixed(3),
          jobs[k].el.textContent.trim().slice(0, 40), wrapper);
      }
    }
  }

  /* ------------------------------------------------------------------------
     Scheduling

     A single pass at load time measures almost nothing on this theme. Images
     below the fold are loading="lazy", so at DOMContentLoaded they have no
     intrinsic size and no bitmap, and the sections holding them animate in on
     scroll. Verified against the live homepage: a one-shot scan applied zero
     verdicts, while the same scan run after scrolling applied eight.

     So the work is driven by an IntersectionObserver over the media itself.
     Each image is measured once, as it comes within a screen of the viewport
     and after its bitmap is actually available.
     ---------------------------------------------------------------------- */

  var queue = new Set();
  var queueTimer = null;

  function schedule(root) {
    queue.add(root || document.body);
    if (queueTimer) return;
    queueTimer = setTimeout(function () {
      queueTimer = null;
      var roots = Array.from(queue);
      queue.clear();
      roots.forEach(function (r) {
        if (r.isConnected !== false) scan(r);
      });
    }, 60);
  }

  /* The subtree worth re-measuring when one piece of media settles. */
  function scopeFor(media) {
    return media.closest('[id^="shopify-section"]') || document.body;
  }

  function whenDecoded(media, cb) {
    if (media.tagName.toLowerCase() === 'video') { cb(); return; }
    if (media.complete && media.naturalWidth) { cb(); return; }
    media.addEventListener('load', cb, { once: true });
    media.addEventListener('error', cb, { once: true });
  }

  var observer = null;

  function observe(root) {
    var media = root.querySelectorAll('img, video');
    if (!observer) {
      /* No IntersectionObserver: fall back to measuring everything now. */
      schedule(root);
      return;
    }
    for (var i = 0; i < media.length; i++) observer.observe(media[i]);
  }

  /* ------------------------------------------------------------------------
     Lifecycle
     ---------------------------------------------------------------------- */

  var resizeTimer = null;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      schedule(document.body);
    }, 200);
  }

  function start() {
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          whenDecoded(entry.target, function () { schedule(scopeFor(entry.target)); });
        });
      }, { rootMargin: '300px 0px' });
    }

    observe(document.body);

    /* Backdrops that are CSS background-image rather than <img> are not
       observed above, so still take one pass once everything has settled. */
    window.addEventListener('load', function () {
      schedule(document.body);
    });

    window.addEventListener('resize', onResize, { passive: true });

    /* Deliberately no scroll listener. Everything measured here is text in the
       normal flow, so it travels with its own backdrop and no verdict can
       change while the page scrolls. The one element that genuinely needed
       scroll tracking was the fixed header, and that is now settled
       declaratively — see the §10 note in Alloi_header.liquid. */

    /* Theme editor re-renders a section in place. */
    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target || document.body;
      observe(root);
      schedule(root);
    });
  }

  window.alloiMediaContrast = {
    /* Returns the promise so callers can await a completed pass. */
    refresh: function (root) { return scan(root || document.body); },
    measure: measure,
    debug: function (on) { DEBUG = on !== false; },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
